import React, { useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import Header from './Header';
import Seo from './Seo';
import Footer from './Footer';
import { useUser } from '../contexts/UserContext';
import './BlogIndex.css';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:3001';

export const BLOG_CATEGORIES = [
  'All',
  'Interview Prep',
  'Job Position',
  'Interview Questions',
  'Resume & Cover Letters',
  'Technical Topic',
  'AI Tools & Productivity',
  'General Topic',
];

function formatDate(iso) {
  if (!iso) return '';
  try {
    return new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  } catch { return ''; }
}

function Avatar({ name, src, size = 24 }) {
  const initial = (name || '?').trim().charAt(0).toUpperCase();
  if (src) {
    return <img src={src} alt={name || ''} className="blog-avatar-img" style={{ width: size, height: size }} />;
  }
  return (
    <span className="blog-avatar-fallback" style={{ width: size, height: size, fontSize: Math.round(size * 0.45) }}>
      {initial}
    </span>
  );
}

function BlogCard({ post }) {
  const hasImg = !!post.hero_image_url;
  const shareUrl = `${window.location.origin}/blog/${post.slug}`;
  // The card itself is a <Link>; stop share clicks from navigating into the post.
  const shareOpen = (u) => (e) => { e.preventDefault(); e.stopPropagation(); window.open(u, '_blank', 'noopener,noreferrer'); };
  return (
    <Link to={`/blog/${post.slug}`} className="blog-card">
      <div
        className={`blog-card-hero ${hasImg ? 'with-img' : ''}`}
      >
        {hasImg && <img src={post.hero_image_url} alt="" loading="lazy" />}
        <span className="blog-card-hero-tag">{post.category}</span>
      </div>
      <div className="blog-card-body">
        <div className="blog-card-meta">
          <span>⏱ {post.read_minutes || 1} min</span>
          <span className="blog-card-dot">·</span>
          <span>{formatDate(post.published_at)}</span>
        </div>
        <h3 className="blog-card-title">{post.title}</h3>
        {post.summary && <p className="blog-card-summary">{post.summary}</p>}
        <div className="blog-card-footer-row">
          <div className="blog-card-author">
            <Avatar name={post.author_name} src={post.author_avatar_url} />
            <span className="blog-card-author-name">{post.author_name}</span>
          </div>
          <div className="blog-card-share">
            <button type="button" className="blog-card-share-btn sh-x" title="Share on X" aria-label="Share on X"
              onClick={shareOpen(`https://twitter.com/intent/tweet?url=${encodeURIComponent(shareUrl)}&text=${encodeURIComponent(post.title)}`)}>
              <svg viewBox="0 0 24 24" width="14" height="14" fill="currentColor" aria-hidden="true"><path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/></svg>
            </button>
            <button type="button" className="blog-card-share-btn sh-li" title="Share on LinkedIn" aria-label="Share on LinkedIn"
              onClick={shareOpen(`https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(shareUrl)}`)}>
              <svg viewBox="0 0 24 24" width="14" height="14" fill="currentColor" aria-hidden="true"><path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433a2.062 2.062 0 01-2.063-2.065 2.064 2.064 0 112.063 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/></svg>
            </button>
            <button type="button" className="blog-card-share-btn sh-wa" title="Share on WhatsApp" aria-label="Share on WhatsApp"
              onClick={shareOpen(`https://wa.me/?text=${encodeURIComponent(post.title + ' ' + shareUrl)}`)}>
              <svg viewBox="0 0 24 24" width="14" height="14" fill="currentColor" aria-hidden="true"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.372-.025-.521-.075-.148-.669-1.611-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.71.306 1.263.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.885-9.885 9.885M20.52 3.449C18.24 1.245 15.24 0 12.045 0 5.463 0 .104 5.359.101 11.892c0 2.096.546 4.142 1.588 5.945L0 24l6.335-1.652a11.882 11.882 0 005.71 1.454h.006c6.585 0 11.946-5.359 11.949-11.945a11.821 11.821 0 00-3.48-8.408"/></svg>
            </button>
            <button type="button" className="blog-card-share-btn sh-copy" title="Copy link" aria-label="Copy link"
              onClick={e => { e.preventDefault(); e.stopPropagation(); navigator.clipboard?.writeText(shareUrl); const b = e.currentTarget; b.classList.add('copied'); setTimeout(() => b.classList.remove('copied'), 1500); }}>
              <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M10 13a5 5 0 007.54.54l3-3a5 5 0 00-7.07-7.07l-1.72 1.71"/><path d="M14 11a5 5 0 00-7.54-.54l-3 3a5 5 0 007.07 7.07l1.71-1.71"/></svg>
            </button>
          </div>
        </div>
      </div>
    </Link>
  );
}

export default function BlogIndex() {
  const { user } = useUser();
  const [searchParams, setSearchParams] = useSearchParams();
  const [posts, setPosts] = useState([]);
  const [total, setTotal] = useState(0);
  const [authors, setAuthors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const category = searchParams.get('category') || 'All';
  const author = searchParams.get('author') || '';
  const q = searchParams.get('q') || '';
  const sort = searchParams.get('sort') || 'newest';

  const [searchInput, setSearchInput] = useState(q);
  useEffect(() => { setSearchInput(q); }, [q]);

  const queryString = useMemo(() => {
    const p = new URLSearchParams();
    if (category && category !== 'All') p.set('category', category);
    if (author) p.set('author', author);
    if (q) p.set('q', q);
    if (sort) p.set('sort', sort);
    return p.toString();
  }, [category, author, q, sort]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError('');
    fetch(`${API_BASE_URL}/api/blog?${queryString}`)
      .then(r => r.json())
      .then(data => {
        if (cancelled) return;
        setPosts(data.posts || []);
        setTotal(data.total || 0);
        setAuthors(data.authors || []);
      })
      .catch(() => !cancelled && setError('Could not load posts. Try refreshing.'))
      .finally(() => !cancelled && setLoading(false));
    return () => { cancelled = true; };
  }, [queryString]);

  const updateParam = (key, value) => {
    const next = new URLSearchParams(searchParams);
    if (value && value !== 'All') next.set(key, value);
    else next.delete(key);
    setSearchParams(next, { replace: true });
  };

  const onSearchSubmit = (e) => {
    e.preventDefault();
    updateParam('q', searchInput.trim());
  };

  return (
    <div className="blog-index-page">
      <Seo
        title="Career & Interview Prep Blog"
        description="Actionable guides on acing interviews, beating ATS resume scans, writing cover letters, and landing your next job — from Terview AI."
        path="/blog"
      />
      {/* Ambient animated background — three drifting blobs + faint starfield.
          Sits behind everything (z-index: 0) while content uses z-index: 1. */}
      <div className="blog-bg-stars" aria-hidden="true" />
      <div className="blog-bg-orb" aria-hidden="true" />

      <Header />

      <main className="blog-index-main">
        <section className="blog-hero">
          <span className="blog-hero-eyebrow">Terview Blog</span>
          <h1 className="blog-hero-title">
            Win the <span className="accent">job market</span>.
          </h1>
          <p className="blog-hero-sub">
            Fresh writing on interviews, resumes, and AI tools that get you hired —
            no fluff, no listicles, no LinkedIn-bro energy.
          </p>
        </section>

        {/* Top toolbar: search + author + sort + count + admin CTA */}
        <div className="blog-toolbar">
          <form onSubmit={onSearchSubmit} className="blog-search">
            <span className="blog-search-icon">🔍</span>
            <input
              type="text"
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              placeholder="Search posts…"
            />
          </form>
          <select
            className="blog-filter-select"
            value={author}
            onChange={(e) => updateParam('author', e.target.value)}
          >
            <option value="">All authors</option>
            {authors.map(a => (
              <option key={a.id} value={a.id}>{a.name}</option>
            ))}
          </select>
          <select
            className="blog-filter-select"
            value={sort}
            onChange={(e) => updateParam('sort', e.target.value)}
          >
            <option value="newest">Newest first</option>
            <option value="oldest">Oldest first</option>
          </select>
          <div className="blog-toolbar-meta">
            <span className="blog-index-count">
              {posts.length} of {total}
            </span>
            {user?.is_admin && (
              <Link to="/blog/admin/new" className="blog-admin-cta">
                + New post
              </Link>
            )}
          </div>
        </div>

        {/* Category chips, inline (no separate card) */}
        <div className="blog-category-chips">
          {BLOG_CATEGORIES.map(c => (
            <button
              key={c}
              type="button"
              className={`blog-chip ${category === c ? 'active' : ''}`}
              onClick={() => updateParam('category', c)}
            >
              {c}
            </button>
          ))}
        </div>

        {/* Card grid */}
        <div className="blog-cards">
          {loading ? (
            <div className="blog-state">Loading posts…</div>
          ) : error ? (
            <div className="blog-state error">{error}</div>
          ) : posts.length === 0 ? (
            <div className="blog-state">
              No posts found{q ? ` for "${q}"` : ''}.
            </div>
          ) : (
            posts.map(p => <BlogCard key={p.id} post={p} />)
          )}
        </div>
      </main>

      <Footer />
    </div>
  );
}
